"use client"
import MainContainer from "@/ui/MainContainer"
import { menuNavigationClick } from "@/utils/gtm"
import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import "swiper/css"
import "swiper/css/free-mode"
import { FreeMode } from "swiper/modules"
import type { SwiperRef } from "swiper/react"
import { Swiper, SwiperSlide } from "swiper/react"
import { mediaQueries } from "../../theme/mediaQueries"
import { colors, sizes } from "../../theme/theme"
import AnchorLink from "./AnchorLink"

const StyledNavContainer = styled(MainContainer, {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>`
  position: fixed;
  border-radius: 0px 0px ${sizes.baseDouble} ${sizes.baseDouble};
  background-color: ${colors.beige};
  box-shadow: 0px 4px ${sizes.baseDouble} 0px rgba(90, 90, 90, 0.2);
  z-index: 99;
  width: 90%;
  overflow-x: hidden;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: translateY(${(props) => (props.visible ? "0" : "-100%")});
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  ${mediaQueries.tabletDown} {
    top: 78px;
    padding: 10px ${sizes.baseDouble};
  }
  ${mediaQueries.desktopM} {
    top: 82px;
    padding: 23px 40px;
  }
  ${mediaQueries.desktopXl} {
    top: 89px;
  }
`

const StyledNavList = styled(Swiper)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${sizes.baseQuadric};
  width: 100%;
  overflow-x: auto;
  -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  /* Add horizontal scroll shadows */
  position: relative;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    height: 100%;
    width: 25px;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, rgba(243, 243, 238, 1), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, rgba(243, 243, 238, 1), transparent);
  }
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome, Safari and Opera */
  }
  scroll-behavior: smooth;
  ${mediaQueries.tabletDown} {
    gap: 20px;
  }
  &.swiper {
    padding: 0;
  }
  .swiper-wrapper {
    display: flex;
    justify-content: flex-start;
  }
`

const StyledNavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>`
  color: ${(props) => (props.active ? colors.red : "transparent")};
  transition: all 0.2s ease-in-out;
  display: flex;
  a {
    color: ${(props) => (props.active ? colors.red : colors.black)};
    text-decoration: none;
    font-size: 16px;
    white-space: nowrap;
    font-weight: ${(props) => (props.active ? "500" : "400")};
    transition: color 0.2s ease-in-out;
    ${mediaQueries.tabletDown} {
      font-size: ${sizes.fonts.body};
    }
    &:hover {
      color: ${colors.red};
    }
  }
`

interface Anchor {
  id: string
  title: string
}

interface AnchorNavigationProps {
  anchors: Anchor[]
}

const PageInnerNavBar = ({ anchors }: AnchorNavigationProps) => {
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null)
  const [scrollOffset, setScrollOffset] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const swiperRef = useRef<SwiperRef>(null)
  // Ensure first anchor is marked with "first": true
  const processedAnchors = anchors.map((anchor, index) => ({
    ...anchor,
    first: index === 0,
  }))
  const handleClick = (buttonText: string) => {
    menuNavigationClick({
      menuType: "תפריט סטיקי",
      menuCategory1: buttonText,
    })
  }
  useEffect(() => {
    const handleScroll = () => {
      const viewportHeight = window.innerHeight
      let currentAnchor: string | null = null
      // Set current scroll position
      setScrollOffset(window.pageYOffset || document.documentElement.scrollTop)

      // Skip the loop if anchors array is empty
      if (!processedAnchors || processedAnchors.length === 0) return

      // Find the anchor that should be active based on scroll position
      for (let i = 0; i < processedAnchors.length; i++) {
        const current = processedAnchors[i]
        if (!current || !current.id) continue

        const element = document.getElementById(current.id)
        if (!element) continue

        const rect = element.getBoundingClientRect()

        // Check if this element is currently in the viewport center area
        if (
          rect.top <= viewportHeight / 2 &&
          rect.bottom > viewportHeight / 2
        ) {
          currentAnchor = current.id
          break
        }

        // If we're past the viewport center, use the previous anchor
        if (rect.top > viewportHeight / 2) {
          if (i > 0) {
            currentAnchor = processedAnchors[i - 1].id
          }
          break
        }

        // If this is the last anchor and we haven't found one yet, use it
        if (
          i === processedAnchors.length - 1 &&
          rect.bottom <= viewportHeight
        ) {
          currentAnchor = current.id
        }
      }

      // If no anchor was determined but we have anchors, default to first
      if (!currentAnchor && processedAnchors.length > 0) {
        currentAnchor = processedAnchors[0].id
      }

      if (currentAnchor !== activeAnchor) {
        setActiveAnchor(currentAnchor)

        // Scroll Swiper to active anchor if it's already set
        if (currentAnchor && swiperRef.current && swiperRef.current.swiper) {
          const swiperInstance = swiperRef.current.swiper
          const activeIndex = processedAnchors.findIndex(
            (anchor) => anchor.id === currentAnchor,
          )
          if (activeIndex !== -1) {
            swiperInstance.slideTo(activeIndex)
          }
        }
      }
    }
    window.addEventListener("scroll", handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [processedAnchors, activeAnchor])

  // Effect to handle visibility based on scrollOffset
  useEffect(() => {
    setIsVisible(scrollOffset > 10)
  }, [scrollOffset])

  // Function to handle anchor click
  const handleAnchorClick = (anchorId: string) => {
    setActiveAnchor(anchorId)

    // Scroll Swiper to the clicked anchor
    if (swiperRef.current && swiperRef.current.swiper) {
      const swiperInstance = swiperRef.current.swiper
      const activeIndex = processedAnchors.findIndex(
        (anchor) => anchor.id === anchorId,
      )
      if (activeIndex !== -1) {
        swiperInstance.slideTo(activeIndex)
      }
    }
  }

  if (processedAnchors.length <= 1) {
    return null // Don't show navigation if there's only one or zero anchors
  }

  return (
    <StyledNavContainer visible={isVisible}>
      <StyledNavList
        ref={swiperRef}
        modules={[FreeMode]}
        spaceBetween={20}
        slidesPerView="auto"
        freeMode
        cssMode={false}
        watchSlidesProgress
      >
        {processedAnchors.map((anchor) => {
          if (!anchor.title || !anchor.id) return null
          return (
            <SwiperSlide key={anchor.id} style={{ width: "auto" }}>
              <StyledNavItem active={activeAnchor === anchor.id}>
                <AnchorLink
                  href={`#${anchor.id}`}
                  onClick={() => {
                    handleAnchorClick(anchor.id)
                    handleClick(anchor.title)
                  }}
                >
                  {anchor.title}
                </AnchorLink>
              </StyledNavItem>
            </SwiperSlide>
          )
        })}
      </StyledNavList>
    </StyledNavContainer>
  )
}

export default PageInnerNavBar
